import React, { useState, useEffect } from 'react';
import { Plus, Minus, ShieldCheck, Bell, Flame, Droplets, Settings, Camera } from 'lucide-react';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const QuickAddAccessories = ({ onAccessoriesChange }) => {
  const [accessories, setAccessories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState({});

  // Predefined quick-add items with their product IDs
  const quickAddConfig = [
    { 
      category: 'Détecteurs',
      icon: ShieldCheck,
      items: [
        { id: 'prod_a8e6c6223bc6', label: 'MotionProtect', shortLabel: 'Mouvement' },
        { id: 'prod_6c7a5faf1fa5', label: 'MotionCam HDR', shortLabel: 'Mouvement + Photo', highlight: true },
        { id: 'prod_6e9ebb9d95e8', label: 'DoorProtect', shortLabel: 'Ouverture' },
        { id: 'prod_180e15a1a143', label: 'GlassProtect', shortLabel: 'Bris de vitre' },
      ]
    },
    {
      category: 'Sirènes',
      icon: Bell,
      items: [
        { id: 'prod_b21a39325e1c', label: 'HomeSiren', shortLabel: 'Intérieure' },
        { id: 'prod_279e54687856', label: 'StreetSiren', shortLabel: 'Extérieure' },
      ]
    },
    {
      category: 'Accessoires',
      icon: Settings,
      items: [
        { id: 'prod_40df4d45e25a', label: 'SpaceControl', shortLabel: 'Télécommande' },
        { id: 'prod_9bc18ccae30a', label: 'KeyPad', shortLabel: 'Clavier' },
        { id: 'prod_69ffa5e0478e', label: 'ReX 2', shortLabel: 'Répéteur' },
      ]
    },
    {
      category: 'Sécurité',
      icon: Flame,
      items: [
        { id: 'prod_205037b0e706', label: 'FireProtect', shortLabel: 'Fumée' },
        { id: 'prod_44fdb9811226', label: 'LeaksProtect', shortLabel: 'Inondation' },
      ]
    },
  ];

  useEffect(() => {
    const fetchAccessories = async () => {
      try {
        const response = await fetch(`${API}/products?category=basic`);
        if (response.ok) {
          const data = await response.json();
          setAccessories(data);
        }
      } catch (error) {
        console.error('Failed to fetch accessories:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAccessories();
  }, []);

  const getProductInfo = (productId) => {
    return accessories.find(p => p.product_id === productId);
  };

  const updateQuantity = (productId, delta) => {
    setSelectedItems(prev => {
      const current = prev[productId] || 0;
      const newQty = Math.max(0, Math.min(10, current + delta));
      
      const updated = { ...prev };
      if (newQty === 0) {
        delete updated[productId];
      } else {
        updated[productId] = newQty;
      }
      
      // Notify parent of changes
      if (onAccessoriesChange) {
        const itemsWithDetails = Object.entries(updated).map(([id, qty]) => {
          const product = getProductInfo(id);
          return product ? {
            product_id: id,
            name: product.name,
            price: product.price,
            quantity: qty,
          } : null;
        }).filter(Boolean);
        
        const total = itemsWithDetails.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        onAccessoriesChange(itemsWithDetails, total);
      }
      
      return updated;
    });
  };

  const totalItems = Object.values(selectedItems).reduce((sum, qty) => sum + qty, 0);
  const totalPrice = Object.entries(selectedItems).reduce((sum, [id, qty]) => {
    const product = getProductInfo(id);
    return sum + (product ? product.price * qty : 0);
  }, 0);

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-8 bg-zinc-800 rounded w-1/3" />
        <div className="h-24 bg-zinc-800 rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="quick-add-accessories">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
          Ajouts rapides
        </h4>
        {totalItems > 0 && (
          <span className="text-xs text-emerald-400 font-medium">
            {totalItems} article{totalItems > 1 ? 's' : ''} • +{totalPrice.toFixed(2)}€
          </span>
        )}
      </div>

      <div className="space-y-3">
        {quickAddConfig.map((group) => (
          <div key={group.category} className="bg-zinc-950/50 border border-zinc-800/60 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-3">
              <group.icon className="w-4 h-4 text-white/50" />
              <span className="text-xs font-medium text-white/50 uppercase tracking-wider">{group.category}</span>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              {group.items.map((item) => {
                const product = getProductInfo(item.id);
                if (!product) return null;
                
                const qty = selectedItems[item.id] || 0;
                const isSelected = qty > 0;
                
                return (
                  <div 
                    key={item.id}
                    className={`relative p-2.5 rounded-lg border transition-all ${
                      isSelected 
                        ? 'bg-emerald-500/10 border-emerald-500/30' 
                        : item.highlight 
                          ? 'bg-purple-500/5 border-purple-500/20 hover:border-purple-500/40'
                          : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'
                    }`}
                  >
                    {item.highlight && !isSelected && (
                      <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 bg-purple-500 text-white text-[8px] font-bold rounded">
                        HDR
                      </span>
                    )}
                    
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-xs font-medium truncate">{item.shortLabel}</p>
                        <p className="text-white/50 text-[10px]">{product.price.toFixed(2)}€</p>
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {isSelected ? (
                          <>
                            <button
                              onClick={() => updateQuantity(item.id, -1)}
                              className="w-6 h-6 rounded bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-white transition-colors"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="w-5 text-center text-white text-xs font-bold">{qty}</span>
                            <button
                              onClick={() => updateQuantity(item.id, 1)}
                              className="w-6 h-6 rounded bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-white transition-colors"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => updateQuantity(item.id, 1)}
                            className="w-6 h-6 rounded bg-emerald-500/20 hover:bg-emerald-500/30 flex items-center justify-center text-emerald-400 transition-colors"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Cameras notice */}
      <div className="flex items-center gap-2 p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg">
        <Camera className="w-4 h-4 text-purple-400 flex-shrink-0" />
        <p className="text-xs text-white/50">
          <span className="text-purple-400 font-medium">Caméras ?</span> Consultez notre gamme vidéoprotection ou demandez un devis.
        </p>
      </div>
    </div>
  );
};

export default QuickAddAccessories;
